import {useRef, useState} from "react";
import {Button, ButtonGroup, Menu, MenuItem} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {useTranslation} from "react-i18next";

interface Props {
    /** Label of the main (silent) action, e.g. "Save" or "Move anyway". */
    label: string;
    /** `sendEmail` carries the staff's explicit intent — the main action always passes false. */
    onConfirm: (sendEmail: boolean) => void;
    color?: "primary" | "secondary" | "error";
    disabled?: boolean;
    loading?: boolean;
    /**
     * Hides the dropdown when this drop cannot send a guest email on its own
     * (halfboard drags and grouped reservations get their email choice later,
     * in the reservation dialog resp. the linked-reservations dialog).
     */
    showEmailOption?: boolean;
}

/**
 * Split "Save ▾" button used by the floorplan drag & drop dialogs: the main action saves
 * silently, the menu item saves and sends the "reservation updated" transactional email.
 * Mirrors the save options of the reservation dialog and the calendar's
 * ConfirmReservationUpdateDialog, so a guest email is never sent by accident.
 */
const SaveWithEmailSplitButton: React.FC<Props> = ({
                                                       label,
                                                       onConfirm,
                                                       color = "primary",
                                                       disabled = false,
                                                       loading = false,
                                                       showEmailOption = true,
                                                   }) => {
    const {t} = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const groupRef = useRef<HTMLDivElement>(null);

    if (!showEmailOption) {
        return (
            <LoadingButton
                variant="contained"
                color={color}
                disabled={disabled}
                loading={loading}
                onClick={() => onConfirm(false)}
            >
                {label}
            </LoadingButton>
        );
    }

    return (
        <>
            <ButtonGroup ref={groupRef} variant="contained" color={color}>
                <LoadingButton
                    variant="contained"
                    color={color}
                    disabled={disabled}
                    loading={loading}
                    onClick={() => onConfirm(false)}
                >
                    {label}
                </LoadingButton>
                <Button
                    variant="contained"
                    color={color}
                    size="small"
                    sx={{px: 0.5, minWidth: 'auto'}}
                    disabled={disabled || loading}
                    onClick={() => setMenuOpen(true)}
                >
                    <ArrowDropDownIcon/>
                </Button>
            </ButtonGroup>
            <Menu
                anchorEl={groupRef.current}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
            >
                <MenuItem onClick={() => {
                    setMenuOpen(false);
                    onConfirm(true);
                }}>
                    {t('Save & send email')}
                </MenuItem>
            </Menu>
        </>
    );
};

export default SaveWithEmailSplitButton;
